// render markdown text

import ReactMarkdown from "react-markdown";
import { Flex, Heading, ListItem, OrderedList, UnorderedList } from "@chakra-ui/react";

/**
 * Render markdown text to jsx
 * 
 * @param {Object} props
 * @param {string} props.children - The markdown text to render.
 *
 * Warning: jsx likes to strip out the newlines, so we need to pass in the text as a variable e.g. 
 * 
 * &lt;MarkdownText&gt;{foo}&lt;/MarkdownText&gt;
 * 
 * Or if it's static text, wrap it in a template string. E.g.
 *
 * &lt;MarkdownText&gt;{`
 * - Hello
 * - World
 * `}&lt;/MarkdownText&gt;
 * 
 
 * @param {Object?} props.components Optional: replace default components by tag, e.g. a: MyLinkWidget
 */
const MarkdownText = ({ children, color, fontSize, fontWeight, components, allowHtml }) => {
  // default components, which can be overridden by the caller
  components = Object.assign(
    {},
    {
      h1: ({ children }) => <Heading fontSize="xl">{children}</Heading>,
      h2: ({ children }) => <Heading fontSize="lg">{children}</Heading>,
      h3: ({ children }) => <Heading fontSize="md">{children}</Heading>,
      ul: ({ children }) => <UnorderedList>{children}</UnorderedList>,
      ol: ({ children }) => <OrderedList>{children}</OrderedList>,
      li: ({ children }) => <ListItem>{children}</ListItem>,
      blockquote: ({ children }) => <blockquote>BQ2{children}</blockquote>,
      p: ({ children }) => <TextOrTable>{children}</TextOrTable>,
    },
    components
  );
  // HACK! sneak html in via a custom marker and h6
  if (allowHtml && children) {
    components.h6 = ({ children }) => {
      let text;
      if (typeof children === "string") text = children;
      else if (Array.isArray(children)) text = children.filter((child) => typeof child === "string").join("");
      if (text && text.substring && text.substring(0, 4) === "HTML") {
        console.log("html", text);
        let html = text.substring(5); //chop the marker
        html = html.replace(/###### HTML /g, "").replace(/ END-HTML/g, ""); // remove other markers
        return <div dangerouslySetInnerHTML={{ __html: html }} />;
      }
      // just a normal h6
      return <h6>{children}</h6>;
    };
    // mark any html blocks with a custom marker
    function removeNewlines(str) {
      return str.replace(/\n/g, "");
    }
    children = children.replace(
      /^(<[a-zA-Z][^>]*>[\s\S]*?\n)/gm,
      (match, p1) => `###### HTML ${removeNewlines(p1)} END-HTML`
    );
  }

  return (
    <Flex color={color} fontSize={fontSize} fontWeight={fontWeight} direction="column" gap={2}>
      <ReactMarkdown components={components}>{children}</ReactMarkdown>
    </Flex>
  );
};

function TextOrTable({ children }) {
  if (!children || children[0] !== "|") {
    return <p>{children}</p>;
  }
  const rows = children
    .split("\n")
    .filter((row) => !row.match(/^[|-]+$/))
    .map((row) => row.split("|").map((cell) => cell.trim()));
  return (
    <table>
      <tbody>
        {rows.map((row, index) => (
          <tr key={index}>
            {row.map((cell, index) => (
              <td key={index}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default MarkdownText;
